import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  structuredData?: object;
}

const useSEO = ({
  title = "Bethanchok Homestay | Best Nepal Homestay Experience | Traditional Village Stay",
  description = "Experience authentic Nepali hospitality at Bethanchok Homestay. Traditional village accommodation in Nepal with stunning mountain views, cultural experiences, yoga, meditation, and trekking. Book your authentic Nepal homestay today!",
  keywords = "Nepal homestay, Bethanchok homestay, Nepal tourism, traditional Nepal accommodation, Nepal village stay, authentic Nepal experience, Nepal cultural tourism, mountain views Nepal, trekking Nepal, yoga retreat Nepal, meditation Nepal",
  image = "https://bethanchokstay.com/images/bethanchok-logo.png",
  url = "https://bethanchokstay.com/",
  type = "website",
  structuredData
}: SEOProps) => {
  useEffect(() => {
    // Update document title
    document.title = title;    // Function to update or create meta tag
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (isProperty) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Update or create link tag
    const updateLinkTag = (rel: string, href: string) => {
      let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', 'Bethanchok Homestay');
    updateMetaTag('robots', 'index, follow');
    
    // Geographic meta tags
    updateMetaTag('geo.region', 'NP');
    updateMetaTag('geo.country', 'Nepal');
    updateMetaTag('geo.placename', 'Bethanchok, Nepal');
    updateMetaTag('ICBM', '27.8, 85.6');

    // Open Graph meta tags
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', url, true);
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:image:width', '1200', true);
    updateMetaTag('og:image:height', '630', true);
    updateMetaTag('og:site_name', 'Bethanchok Homestay', true);
    updateMetaTag('og:locale', 'en_US', true);

    // Twitter meta tags
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:url', url, true);
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);
    updateMetaTag('twitter:image', image, true);

    // Canonical URL
    updateLinkTag('canonical', url);

    // Structured data
    if (structuredData) {
      let script = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(structuredData);
    }

    // Cleanup function to remove dynamically added elements when component unmounts
    return () => {
      // Note: In a SPA, we typically don't remove meta tags as they might be needed by other pages
      // But we could implement a cleanup strategy if needed
    };
  }, [title, description, keywords, image, url, type, structuredData]);
};

export default useSEO;
